import { useParams, useNavigate } from 'react-router-dom'
import { Accordion, Breadcrumb, Loader, Separator, Table } from '@/ui'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import {
  AdminImportedTransaction,
  FETCH_ADMIN_IMPORTED_TRANSACTIONS_KEY,
  fetchAdminImportedTransactions,
} from '@/actions/admin/transactionActions'
import { centsToDollars } from '@/utils/currencyHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON, formatISOFromUTC } from '@/utils/dateHelpers'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'

import { UserBreadcrumbs } from '../user-record/UserBreadcrumbs'
import UserTransactionsHeader from './UserTransactionsHeader'
import { UserFreeTrialAlert } from '../user-record/UserAccountStates/UserFreeTrialAlert'

const getAccountName = (financialAccount?: Partial<FinancialAccount>) => {
  let accountName = 'N/A'
  if (financialAccount) {
    const tokens = [
      financialAccount?.plaidInstitutionName,
      financialAccount?.name,
      financialAccount?.mask,
    ]
    if (tokens.every((token) => token)) {
      accountName = `${tokens[0]}: ${tokens[1]} ${tokens[2]}`
    } else if (tokens[0]) {
      accountName = `${tokens[0]}: ${tokens.slice(1).join(' ').trim()}`
    } else {
      accountName = tokens.slice(1).join(' ').trim()
    }
  }
  return accountName
}

const TransactionTableRow = ({
  transaction,
}: {
  transaction: AdminImportedTransaction
}) => (
  <Table.Row>
    <Table.Cell>{transaction.id}</Table.Cell>
    <Table.Cell>
      {formatISOFromUTC(transaction.date, DATE_FORMATS_LUXON.DISPLAY_SHORT)}
    </Table.Cell>
    <Table.Cell>{getAccountName(transaction.financialAccount)}</Table.Cell>
    <Table.Cell>{transaction.description}</Table.Cell>
    <Table.Cell className="text-right">
      <CurrencyFormatLabel value={centsToDollars(transaction.amountInCents)} />
    </Table.Cell>
  </Table.Row>
)

const ImportAccordionList = ({
  accountName,
  title,
  transactions,
}: {
  accountName: string
  title: string
  transactions: AdminImportedTransaction[]
}) => {
  return (
    <Accordion.Item value={title}>
      <Accordion.Trigger badge={accountName} badgeColor="green">
        Imported on: {title}
      </Accordion.Trigger>

      <Accordion.Content>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>tId</Table.HeaderCell>
              <Table.HeaderCell width={2}>Date</Table.HeaderCell>
              <Table.HeaderCell width={3}>Account</Table.HeaderCell>
              <Table.HeaderCell width={8}>Description</Table.HeaderCell>
              <Table.HeaderCell width={2}>Amount</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {transactions.map((transaction) => (
              <TransactionTableRow
                key={transaction.id}
                transaction={transaction}
              />
            ))}
          </Table.Body>
        </Table>
      </Accordion.Content>
    </Accordion.Item>
  )
}

const ImportedTransactionsPanel = () => {
  const { userId } = useParams()
  const loading = useReselector(selectIsFetchingForKeys, [
    FETCH_ADMIN_IMPORTED_TRANSACTIONS_KEY,
  ])
  const importedTransactions = useFetchResponse(
    fetchAdminImportedTransactions,
    userId
  )
  const navigate = useNavigate()
  if (loading) {
    return <Loader />
  }
  return (
    <div>
      <UserBreadcrumbs userId={userId}>
        <Breadcrumb.Separator />
        <Breadcrumb.Item>
          <Breadcrumb.Link
            onClick={() => {
              navigate(`/admin/finances/transactions/${userId}`)
            }}
          >
            Transactions
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        <Breadcrumb.Separator />
        <Breadcrumb.Item>
          <Breadcrumb.Page>Imported</Breadcrumb.Page>
        </Breadcrumb.Item>
      </UserBreadcrumbs>
      <UserFreeTrialAlert userId={userId} />
      <Separator />

      <UserTransactionsHeader
        backlink={`/admin/finances/transactions/${userId}`}
        headerDescription={'Manually Imported Transactions'}
      />
      {importedTransactions && Object.keys(importedTransactions).length > 0 && (
        <Accordion type="multiple" variant="header" collapsible>
          {Object.keys(importedTransactions).map((transKey) => {
            const subTransactions = importedTransactions[transKey]
            return (
              <ImportAccordionList
                title={transKey}
                transactions={subTransactions}
                key={transKey}
                accountName={getAccountName(
                  subTransactions[0].financialAccount
                )}
              />
            )
          })}
        </Accordion>
      )}
      {!importedTransactions ||
        (Object.keys(importedTransactions).length === 0 && (
          <div>
            <p>No manually imported transactions.</p>
          </div>
        ))}
    </div>
  )
}

export default ImportedTransactionsPanel
